import { useEffect, useState } from 'react';
import {
  authApi,
  mergeStoredUser,
  NOTIFICATIONS_UPDATED_EVENT,
  readStoredToken,
  readStoredUser as readStoredAuthUser,
  SPADES_UPDATED_EVENT,
} from '../services/authApi';

export function readStoredUser() {
  return readStoredAuthUser();
}

export function syncStoredUser(updates) {
  mergeStoredUser(updates);
}

function buildNavbarUser(notificationCount) {
  const storedUser = readStoredUser();

  return {
    id: storedUser?.id || null,
    name: storedUser?.username || storedUser?.firstName || 'StreamerPro',
    avatar: storedUser?.profileImage || null,
    spades: typeof storedUser?.spadesBalance === 'number' ? storedUser.spadesBalance : 0,
    notifications: notificationCount,
  };
}

export function useNavbarUser({ notificationCount = 0, fetchBalance = true, fetchNotifications = true } = {}) {
  const [user, setUser] = useState(() => buildNavbarUser(notificationCount));

  useEffect(() => {
    setUser((prev) => ({ ...prev, notifications: notificationCount }));
  }, [notificationCount]);

  useEffect(() => {
    const handleSpadesUpdated = (event) => {
      const nextBalance = event?.detail?.spadeBalance;
      if (typeof nextBalance !== 'number' || Number.isNaN(nextBalance)) {
        return;
      }

      setUser((prev) => ({ ...prev, spades: nextBalance }));
    };

    window.addEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    return () => {
      window.removeEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    };
  }, []);

  useEffect(() => {
    const handleNotificationsUpdated = (event) => {
      const nextCount = event?.detail?.unreadCount;
      if (typeof nextCount !== 'number' || Number.isNaN(nextCount)) {
        return;
      }

      setUser((prev) => ({ ...prev, notifications: nextCount }));
    };

    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    };
  }, []);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      return undefined;
    }

    let cancelled = false;

    const syncNavbarUser = async () => {
      try {
        const [userResponse, balanceResponse, notificationResponse] = await Promise.all([
          authApi.getCurrentUser(token).catch(() => null),
          fetchBalance ? authApi.getPaymentBalance(undefined, token).catch(() => null) : Promise.resolve(null),
          fetchNotifications ? authApi.getNotificationUnreadCount(token).catch(() => null) : Promise.resolve(null),
        ]);

        if (cancelled) {
          return;
        }

        const currentUser = userResponse?.user;
        if (currentUser) {
          syncStoredUser(currentUser);
        }

        setUser((prev) => ({
          ...prev,
          id: currentUser?.id || prev.id,
          name: currentUser?.username || currentUser?.firstName || prev.name,
          avatar: currentUser?.profileImage || prev.avatar,
          spades: typeof balanceResponse?.spade_balance === 'number'
            ? balanceResponse.spade_balance
            : prev.spades,
          notifications: typeof notificationResponse?.unreadCount === 'number'
            ? notificationResponse.unreadCount
            : prev.notifications,
        }));
      } catch (error) {
        console.error('Failed to sync navbar user:', error);
      }
    };

    syncNavbarUser();

    return () => {
      cancelled = true;
    };
  }, [fetchBalance, fetchNotifications, notificationCount]);

  return user;
}
